import { ObservationModel } from '@ctw/shared/api/fhir/models/observation';
import type { PatientModel } from '@ctw/shared/api/fhir/models/patient';
import {
  type FqsRequestOptions,
  fqsRequest,
  fqsRequestAll,
  MAX_OBJECTS_PER_REQUEST,
} from '@ctw/shared/api/fqs/client';
import {
  type ObservationGraphqlResponse,
  type ObservationLiteGraphqlResponse,
  observationLiteQuery,
  observationQuery,
} from '@ctw/shared/api/fqs/queries/observations';
import { usePatientQuery } from '@ctw/shared/context/patient-provider';
import type { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { LOINC_TREND_EXCLUSIONS } from '@ctw/shared/utils/loinc';
import { QUERY_KEY_PATIENT_OBSERVATIONS } from '@ctw/shared/utils/query-keys';
import { TrendAnalytes, type ValueSetKey, ValueSets } from '@ctw/shared/utils/value-sets';
import type { GraphQLClient } from 'graphql-request';
import { compact, sortBy, uniq } from 'lodash-es';
import { useEffect, useState } from 'react';
import { SYSTEM_LOINC, SYSTEM_SUMMARY } from './system-urls';

// Uses the lightweight ObservationLite fragment (omits component, encounter,
// category, status, ProvenanceList, valueRatio, subject.resource, and top-level
// extension — none of which are read by the DR drawer's ObservationList or the
// lab writeback modal). For the generic resource-fetch path (deep link / pushView
// into an Observation drawer), use `fetchObservationsById` directly — that keeps
// the full fragment which getDataSourceDisplay relies on.
export function usePatientObservationsById(ids: Array<string>) {
  return usePatientQuery({
    queryId: QUERY_KEY_PATIENT_OBSERVATIONS,
    queryKey: [ids],
    queryFn: async ({ graphqlClient, telemetry, patient }) => {
      if (ids.length === 0) {
        return [];
      }
      return await fetchObservationsByIdLite(telemetry, graphqlClient, patient, ids, {
        requestHeaders: {
          'X-Use-Hotstorage': '1',
        },
      });
    },
    enabled: ids.length > 0,
  });
}

export function usePatientObservationsWithTrends(ids: Array<string>) {
  const observationsQuery = usePatientObservationsById(ids);
  const [observations, setObservations] = useState<Array<ObservationModel>>([]);

  // Get LOINC codes from observations and include related codes from TrendAnalytes
  const loincCodes = uniq(
    compact(
      observationsQuery.data?.flatMap((obs) => {
        const loincCode = obs.loincCode?.code;
        if (!loincCode) {
          return [];
        }

        const analyteEntry = TrendAnalytes.find((analyte) =>
          ValueSets[analyte]?.content.some(
            (c) => c.code === loincCode && c.system === SYSTEM_LOINC,
          ),
        );

        if (!analyteEntry) {
          return [loincCode];
        }
        return ValueSets[analyteEntry]?.content.map((c) => c.code);
      }),
    ),
  );

  const trendObservationsQuery = usePatientObservationsByLoinc(loincCodes);

  useEffect(() => {
    if (observationsQuery.data?.length === 0) {
      return;
    }

    const trendsByAnalyte = (trendObservationsQuery.data ?? []).reduce<
      Record<string, Array<ObservationModel>>
    >((acc, obs) => {
      const loincCode = obs.loincCode?.code;
      if (!loincCode) {
        return acc;
      }

      // Skip if LOINC code is in exclusions list
      if (LOINC_TREND_EXCLUSIONS.includes(loincCode)) {
        return acc;
      }

      // Find which analyte this LOINC code belongs to (eg. A1c, LDL)
      const analyteEntry = TrendAnalytes.find((analyte) =>
        ValueSets[analyte]?.content.some((c) => c.code === loincCode && c.system === SYSTEM_LOINC),
      );

      // If no analyte match found, use the LOINC code itself as the key
      const key = analyteEntry ?? loincCode;
      acc[key] ??= [];

      // Check if we already have an observation with same date and value
      const isDuplicate = acc[key].some(
        (existing: ObservationModel) =>
          existing.date === obs.date && existing.valueWithUnits === obs.valueWithUnits,
      );

      if (!isDuplicate) {
        acc[key].push(obs);
      }

      return acc;
    }, {});

    const observationsWithTrends = observationsQuery.data?.map((obs) => {
      const loincCode = obs.loincCode?.code;
      if (!loincCode) {
        return obs;
      }

      // First try to find analyte match
      const analyteEntry = TrendAnalytes.find((analyte) =>
        ValueSets[analyte]?.content.some((c) => c.code === loincCode && c.system === SYSTEM_LOINC),
      );

      if (analyteEntry) {
        obs.trends = trendsByAnalyte[analyteEntry];
      } else {
        obs.trends = trendsByAnalyte[loincCode];
      }

      return obs;
    });

    setObservations(observationsWithTrends ?? []);
  }, [observationsQuery.data, trendObservationsQuery.data]);

  return {
    data: observations,
    isLoadingObservations: observationsQuery.isLoading,
    isLoadingTrends: trendObservationsQuery.isLoading,
    isError: observationsQuery.isError || trendObservationsQuery.isError,
  };
}

export function usePatientObservationsByLoinc(loincCodes: Array<string>) {
  return usePatientQuery({
    queryId: QUERY_KEY_PATIENT_OBSERVATIONS,
    queryKey: [loincCodes],
    queryFn: async ({ graphqlClient, telemetry, patient }) => {
      if (loincCodes.length === 0) {
        return [];
      }
      const observations = await fetchObservationsByLoinc(
        telemetry,
        graphqlClient,
        patient,
        loincCodes,
        {
          requestHeaders: {
            'X-Use-Hotstorage': '1',
          },
        },
      );
      return observations.filter((vital) => vital.valueWithUnits !== '');
    },
    enabled: loincCodes.length > 0,
  });
}

export type NumericObservationResult = {
  date: string;
  valueDisplay: string;
  valueNumeric: number;
  alertInterpretation?: string;
  referenceRange?: string;
};

export type NumericObservationsGroup = Array<{
  displayName: string;
  data: Array<NumericObservationResult>;
}>;

export function usePatientNumericObservationsByValueSet(valueSets: Array<ValueSetKey>) {
  const loincValueSets = Object.entries(ValueSets)
    .filter(([key]) => valueSets.includes(key as ValueSetKey))
    .map(([key, vs]) => ({
      key,
      display: vs.display,
      loincCodes: vs.content.filter((x) => x.system === SYSTEM_LOINC).map((c) => c.code),
    }));

  const loincCodes = uniq(loincValueSets.flatMap((vs) => vs.loincCodes));
  const observations = usePatientObservationsByLoinc(loincCodes);

  // Group observations by value set display name
  const grouped: Record<string, Array<NumericObservationResult>> = {};

  const numericObservationsWithLoincCode =
    observations.data?.filter((obs) => obs.date && obs.valueNumeric && obs.loincCode) ?? [];

  for (const obs of numericObservationsWithLoincCode) {
    const matchingValueSets = loincValueSets.filter(
      (vs) => obs.loincCode?.code && vs.loincCodes.includes(obs.loincCode?.code),
    );

    for (const vs of matchingValueSets) {
      if (!grouped[vs.display]) {
        grouped[vs.display] = [];
      }
      grouped[vs.display].push({
        date: obs.date as string,
        valueDisplay: obs.valueWithUnits,
        valueNumeric: Number.parseFloat(obs.valueWithoutUnits),
        alertInterpretation: obs.isAlertInterpretation ? obs.interpretation : undefined,
        referenceRange: obs.referenceRange,
      });
    }
  }

  // Sort each group by date and limit to 20 most recent entries
  const sortedFilterd = Object.entries(grouped)
    .map(([displayName, data]) => {
      const sortedData = sortBy(data, 'date').slice(-20);
      return { displayName, data: sortedData };
    })
    .sort((a, b) => a.displayName.localeCompare(b.displayName));

  return {
    data: sortedFilterd,
    isLoading: observations.isLoading,
    isError: observations.isError,
    error: observations.error,
  };
}

export async function fetchObservationsByLoinc(
  telemetry: ReturnType<typeof useTelemetry>,
  graphqlClient: GraphQLClient,
  patient: PatientModel,
  loincCodes: Array<string>,
  options?: FqsRequestOptions,
) {
  const codes = loincCodes.map((loincCode) => `${SYSTEM_LOINC}|${loincCode}`);
  const { data } = await fqsRequest<ObservationGraphqlResponse>(
    telemetry,
    graphqlClient,
    observationQuery,
    {
      upid: patient.UPID,
      cursor: '',
      first: MAX_OBJECTS_PER_REQUEST,
      sort: {
        lastUpdated: 'DESC',
      },
      filter: {
        code: {
          anymatch: codes,
        },
      },
    },
    options,
  );

  const observationModels = data.ObservationConnection.edges.map(
    ({ node }) => new ObservationModel(node, node.ProvenanceList),
  );

  return observationModels;
}

export async function fetchObservationsById(
  telemetry: ReturnType<typeof useTelemetry>,
  graphqlClient: GraphQLClient,
  patient: PatientModel,
  ids: Array<string>,
  options?: FqsRequestOptions,
) {
  const { data } = await fqsRequestAll<ObservationGraphqlResponse>(
    telemetry,
    graphqlClient,
    observationQuery,
    'ObservationConnection',
    {
      upid: patient.UPID,
      cursor: '',
      first: MAX_OBJECTS_PER_REQUEST,
      filter: {
        ids: {
          anymatch: ids,
        },
        tag: { nonematch: [SYSTEM_SUMMARY] },
      },
    },
    undefined,
    options,
  );

  return data.ObservationConnection.edges.map(({ node }) => new ObservationModel(node));
}

/**
 * Fetches observations by ID using the lightweight ObservationLite fragment via
 * the `ObservationConnection(filter: { ids: anymatch })` resolver. Safe for
 * consumers that only read the fields listed on `fragmentObservationLite`
 * — specifically the DR drawer's ObservationList and the lab writeback modal.
 */
async function fetchObservationsByIdLite(
  telemetry: ReturnType<typeof useTelemetry>,
  graphqlClient: GraphQLClient,
  patient: PatientModel,
  ids: Array<string>,
  options?: FqsRequestOptions,
) {
  if (ids.length === 0) {
    return [];
  }
  const { data } = await fqsRequestAll<ObservationLiteGraphqlResponse>(
    telemetry,
    graphqlClient,
    observationLiteQuery,
    'ObservationConnection',
    {
      upid: patient.UPID,
      cursor: '',
      first: Math.max(ids.length, 1),
      // The `tag: { nonematch: [SYSTEM_SUMMARY] }` filter is deliberately omitted here.
      // IDs are selective enough on their own; ANDing with a tag filter forces a
      // server-side tag scan over the entire observation collection and dominates
      // latency (we observed ~21s for 2 IDs on a 150k-observation patient).
      filter: {
        ids: {
          anymatch: ids,
        },
      },
    },
    undefined,
    options,
  );

  return data.ObservationConnection.edges.map(({ node }) => new ObservationModel(node));
}
